/*
 * The three log/history browsers -- AccessChange history, the AppLog table,
 * and the per-application Seq viewer -- share one shape: a filter form that
 * carries data-refresh-url and data-container-id, a pager, and per-row
 * drill-in. They used to live inside the core plumbing file; they are area
 * code and live here, still published under their Mcp.Admin.* action names.
 */
import { ajaxSwap, ajaxJson } from '../core/ajax';
import { closest, field, one } from '../core/dom';
import { serializeQuery } from '../core/forms';
import { toast } from '../core/toast';
import { antiForgeryToken } from '../core/http';

function refreshFromForm(form: HTMLFormElement | null, alertTitle: string): void {
    if (!form) return;
    const url = form.dataset['refreshUrl'];
    const containerId = form.dataset['containerId'];
    if (!url || !containerId) return;
    // replaceWith: the partial's outer <div id=containerId> replaces the current one
    // rather than nesting inside it on every refresh.
    void ajaxSwap({ container: containerId, url: `${url}?${serializeQuery(form)}`, mode: 'replaceWith', alertTitle });
}

function setValue(root: ParentNode, name: string, value: string): void {
    const input = field(root, name);
    if (input) input.value = value;
}

function toggleDetail(row: Element, detailSelector: string, chevronSelector: string): void {
    const table = closest(row, 'table');
    table?.querySelector(detailSelector)?.classList.toggle('d-none');
    row.querySelector(chevronSelector)?.classList.toggle('expanded');
}

// ---------------------------------------------------------------- AccessChange

export const AccessChange = {
    ToggleDetail(this: Element, id: unknown): void {
        toggleDetail(this, `tr.access-change-detail[data-access-change-id="${String(id)}"]`, '.access-change-chevron');
    },
    /**
     * A column header was clicked. Sorting is server-side -- the table is paged,
     * so re-ordering the rows already on screen would mean something other than
     * what the header says -- and the filter form is the channel every refresh
     * already uses, so the chosen column rides along with the rest of the filter.
     * Clicking the sorted column reverses it; any other column starts ascending.
     * Returns false so dispatch swallows the header's href="#" (which would
     * otherwise push a history entry and break Back).
     */
    HeaderClick(this: Element): false {
        const column = this.getAttribute('data-sortcol');
        const form = closest(this, '.access-change-history')?.querySelector<HTMLFormElement>('form.access-change-filter-form') ?? null;
        if (!column || !form) return false;
        const sorted = this.getAttribute('data-sorted') === 'true';
        const ascending = sorted ? this.getAttribute('data-sortasc') === 'false' : true;
        setValue(form, 'SortBy', column);
        setValue(form, 'SortDescending', ascending ? 'false' : 'true');
        // A new order makes the current offset meaningless.
        setValue(form, 'Skip', '0');
        refreshFromForm(form, 'Failed to refresh history');
        return false;
    },
    FilterApply(this: Element): void {
        const form = closest<HTMLFormElement>(this, 'form.access-change-filter-form');
        if (form) setValue(form, 'Skip', '0');
        refreshFromForm(form, 'Failed to refresh history');
    },
    FilterReset(this: Element): void {
        const form = closest<HTMLFormElement>(this, 'form.access-change-filter-form');
        if (!form) return;
        for (const name of ['Action', 'ActorUserName', 'FromUtc', 'ToUtc', 'SortBy']) setValue(form, name, '');
        setValue(form, 'SortDescending', 'true');
        setValue(form, 'Skip', '0');
        refreshFromForm(form, 'Failed to refresh history');
    },
    PagerOlder(this: Element, skip: unknown, take: unknown): void {
        const form = closest(this, '.access-change-history')?.querySelector<HTMLFormElement>('form.access-change-filter-form') ?? null;
        if (!form) return;
        setValue(form, 'Skip', String((Number(skip) || 0) + (Number(take) || 50)));
        refreshFromForm(form, 'Failed to refresh history');
    },
    PagerNewer(this: Element, skip: unknown, take: unknown): void {
        const form = closest(this, '.access-change-history')?.querySelector<HTMLFormElement>('form.access-change-filter-form') ?? null;
        if (!form) return;
        const next = (Number(skip) || 0) - (Number(take) || 50);
        setValue(form, 'Skip', String(next < 0 ? 0 : next));
        refreshFromForm(form, 'Failed to refresh history');
    },
};

// ---------------------------------------------------------------- AppLog

export const AppLog = {
    ToggleDetail(this: Element, id: unknown): void {
        toggleDetail(this, `tr.applog-detail[data-applog-id="${String(id)}"]`, '.applog-chevron');
    },
    FilterApply(this: Element): void {
        const form = closest<HTMLFormElement>(this, 'form.applog-filter-form');
        if (form) setValue(form, 'Page', '1');
        refreshFromForm(form, 'Failed to refresh log');
    },
    FilterReset(this: Element): void {
        const form = closest<HTMLFormElement>(this, 'form.applog-filter-form');
        if (!form) return;
        for (const name of ['FromUtc', 'ToUtc', 'Logger', 'Message', 'UserName']) setValue(form, name, '');
        const levels = field<HTMLSelectElement>(form, 'Levels');
        if (levels) for (const option of Array.from(levels.options)) option.selected = false;
        setValue(form, 'Page', '1');
        refreshFromForm(form, 'Failed to refresh log');
    },
    PagerOlder(this: Element, page: unknown): void {
        const form = closest(this, '.applog-browse')?.querySelector<HTMLFormElement>('form.applog-filter-form') ?? null;
        if (!form) return;
        setValue(form, 'Page', String((Number(page) || 1) + 1));
        refreshFromForm(form, 'Failed to refresh log');
    },
    PagerNewer(this: Element, page: unknown): void {
        const form = closest(this, '.applog-browse')?.querySelector<HTMLFormElement>('form.applog-filter-form') ?? null;
        if (!form) return;
        const prev = (Number(page) || 1) - 1;
        setValue(form, 'Page', String(prev < 1 ? 1 : prev));
        refreshFromForm(form, 'Failed to refresh log');
    },
};

// ---------------------------------------------------------------- ApplicationLog (Seq, cursor-paged)

function logForm(from: Element): HTMLFormElement | null {
    return closest(from, '.application-log')?.querySelector<HTMLFormElement>('form.application-log-filter-form') ?? null;
}

function updateLevelLabel(scope: Element | null): void {
    const dropdown = scope?.querySelector('.application-log-levels');
    if (!dropdown) return;
    const checked = Array.from(dropdown.querySelectorAll<HTMLInputElement>('input.application-log-level:checked'));
    const label = dropdown.querySelector('.application-log-levels-label');
    if (!label) return;
    label.textContent = checked.length === 0 ? 'Level: All' : (checked.length === 1 ? (checked[0]?.value ?? '') : `${checked.length} Levels`);
}

function readHistory(form: HTMLFormElement): string[] {
    const raw = field(form, 'CursorHistory')?.value ?? '';
    return raw ? raw.split(',') : [];
}

function writeHistory(form: HTMLFormElement, stack: string[]): void {
    setValue(form, 'CursorHistory', stack.join(','));
}

export const ApplicationLog = {
    ToggleDetail(this: Element, id: unknown): void {
        toggleDetail(this, `tr.application-log-detail[data-log-id="${String(id)}"]`, '.application-log-chevron');
    },
    LevelChanged(this: Element): void {
        updateLevelLabel(closest(this, '.application-log'));
    },
    FilterApply(this: Element): void {
        const form = closest<HTMLFormElement>(this, 'form.application-log-filter-form');
        if (!form) return;
        const from = field(form, 'FromUtc')?.value ?? '';
        const to = field(form, 'ToUtc')?.value ?? '';
        const today = new Date().toISOString().slice(0, 10);
        if ((from && from > today) || (to && to > today)) {
            toast('warning', 'Invalid date', 'Dates cannot be in the future.');
            return;
        }
        if (from && to && to < from) {
            toast('warning', 'Invalid range', 'The end date must be on or after the start date.');
            return;
        }
        setValue(form, 'AfterId', '');
        setValue(form, 'CursorHistory', '');
        refreshFromForm(form, 'Failed to refresh log');
    },
    FilterReset(this: Element): void {
        const form = closest<HTMLFormElement>(this, 'form.application-log-filter-form');
        if (!form) return;
        for (const input of Array.from(form.querySelectorAll<HTMLInputElement>('input.application-log-level:checked'))) input.checked = false;
        for (const name of ['Keyword', 'FromUtc', 'ToUtc', 'AfterId', 'CursorHistory']) setValue(form, name, '');
        updateLevelLabel(closest(this, '.application-log'));
        refreshFromForm(form, 'Failed to refresh log');
    },
    Older(this: Element): void {
        const afterId = this.getAttribute('data-after-id');
        const form = logForm(this);
        if (!form) return;
        const after = field(form, 'AfterId');
        const current = after?.value ?? '';
        if (current !== '') {
            const stack = readHistory(form);
            stack.push(current);
            writeHistory(form, stack);
        }
        if (after) after.value = afterId ?? '';
        refreshFromForm(form, 'Failed to refresh log');
    },
    Newer(this: Element): void {
        const form = logForm(this);
        if (!form) return;
        const stack = readHistory(form);
        const prev = stack.pop();
        setValue(form, 'AfterId', prev ?? '');
        writeHistory(form, stack);
        refreshFromForm(form, 'Failed to refresh log');
    },
    Newest(this: Element): void {
        const form = logForm(this);
        if (!form) return;
        setValue(form, 'AfterId', '');
        setValue(form, 'CursorHistory', '');
        refreshFromForm(form, 'Failed to refresh log');
    },
    SaveConfig(this: Element): void {
        const form = closest<HTMLFormElement>(this, 'form.application-log-config-form');
        if (!form) return;
        const url = form.dataset['saveUrl'];
        const containerId = form.dataset['containerId'] ?? '';
        if (!url) return;
        void ajaxJson({
            url,
            method: 'POST',
            data: {
                applicationName: field(form, 'applicationName')?.value ?? '',
                property: field(form, 'property')?.value ?? '',
                filterOverride: field(form, 'filterOverride')?.value ?? '',
                enabled: field(form, 'enabled')?.checked ?? false,
                __RequestVerificationToken: antiForgeryToken(),
            },
            onSuccess: () => {
                toast('success', 'Log source saved');
                const filterForm = one(containerId)?.querySelector<HTMLFormElement>('form.application-log-filter-form');
                const reloadUrl = filterForm?.dataset['refreshUrl'] || url.replace(/\/Config$/, '');
                void ajaxSwap({ container: containerId, url: reloadUrl, mode: 'replaceWith', alertTitle: 'Failed to refresh log' });
            },
            onFailure: data => {
                const message = typeof data === 'object' && data !== null && 'message' in data ? String((data as { message?: unknown }).message ?? '') : '';
                toast('danger', 'Save failed', message);
                return true;
            },
            onError: () => { toast('danger', 'Save failed'); return true; },
        });
    },
};
